/*
  C LP Entrepreneur — shared motion and CTA wiring (template page.c-lp-entrepreneur).
  Ports the entrance and count-up behaviour of the design team's prototype
  (claude.ai/artifact/Eaa9MHkdZjHuWMxRVEpggN) without its Motion dependency: IntersectionObserver
  plus the Web Animations API.

  Markup contract (all optional, any c-lp-ent section can use them):
  - [data-lp-reveal] on a container. Its [data-lp-reveal-item] descendants fade and rise in, staggered,
    when 20% of the container is visible. Per-item overrides: data-lp-y (px, default 24),
    data-lp-scale (default 1), data-lp-delay (s), data-lp-duration (s, default .65).
    Container overrides: data-lp-stagger (s, default .08), data-lp-delay (s).
    Items already on screen at load are left alone, as in the prototype (no flash on refresh mid-page).
  - [data-lp-count] on an element whose text is a number ("474,852"). It counts up from 0 every time
    60% of it enters the viewport, and snaps back to the final value when it leaves.
  - a[data-lp-cta] links carry the page's campaign parameters (utm_* and ad click ids) over to their
    destination, and keep their data-placement for analytics. Other params (view, preview_theme_id) stay behind.
  Sections dispatch nothing and need no init call: the script scans on DOMContentLoaded and again on
  shopify:section:load (theme editor).
  prefers-reduced-motion: nothing is hidden or animated, counts show their final value.
*/
(function () {
  'use strict';
  if (window.cLpEntMotion) return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var CAMPAIGN = /^(utm_[a-z]+|gclid|gbraid|wbraid|fbclid|ttclid|msclkid)$/i;

  function num(v, d) {
    var n = parseFloat(v);
    return isNaN(n) ? d : n;
  }
  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight * 0.92 && r.bottom > 0;
  }

  /* ---------- UTMs onto CTAs ---------- */
  function wireCtas(root) {
    var qs = window.location.search;
    if (!qs || qs.length < 2) return;
    var params = new URLSearchParams(qs);
    root.querySelectorAll('a[data-lp-cta]').forEach(function (a) {
      if (a.dataset.lpCtaWired) return;
      a.dataset.lpCtaWired = '1';
      try {
        var url = new URL(a.getAttribute('href'), window.location.origin);
        params.forEach(function (value, key) {
          if (!CAMPAIGN.test(key) || url.searchParams.has(key)) return;
          url.searchParams.set(key, value);
        });
        a.setAttribute('href', url.pathname + url.search + url.hash);
      } catch (e) {}
    });
  }

  /* ---------- staggered reveal ---------- */
  function setupReveal(container) {
    if (container.dataset.lpRevealReady) return;
    container.dataset.lpRevealReady = '1';
    if (reduce || !container.animate) return;
    var items = Array.prototype.slice.call(container.querySelectorAll('[data-lp-reveal-item]'));
    var hidden = items.filter(function (el) { return !inViewport(el); });
    if (!hidden.length) return;
    hidden.forEach(function (el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(' + num(el.dataset.lpY, 24) + 'px) scale(' + num(el.dataset.lpScale, 1) + ')';
    });
    var stagger = num(container.dataset.lpStagger, 0.08);
    var baseDelay = num(container.dataset.lpDelay, 0);
    var io = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      io.disconnect();
      hidden.forEach(function (el, i) {
        var y = num(el.dataset.lpY, 24);
        var s = num(el.dataset.lpScale, 1);
        var anim = el.animate(
          [
            { opacity: 0, transform: 'translateY(' + y + 'px) scale(' + s + ')' },
            { opacity: 1, transform: 'translateY(0px) scale(1)' }
          ],
          {
            duration: num(el.dataset.lpDuration, 0.65) * 1000,
            delay: (baseDelay + num(el.dataset.lpDelay, 0) + i * stagger) * 1000,
            easing: EASE,
            fill: 'backwards'
          }
        );
        el.style.opacity = '';
        el.style.transform = '';
        anim.onfinish = function () { el.style.willChange = ''; };
      });
    }, { threshold: 0.2 });
    io.observe(container);
  }

  /* ---------- count-up ---------- */
  function fmt(n) {
    return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function setupCount(el) {
    if (el.dataset.lpCountReady) return;
    el.dataset.lpCountReady = '1';
    var target = parseInt(String(el.dataset.lpCount || el.textContent).replace(/[^\d]/g, ''), 10);
    if (!target || reduce) return;
    el.style.fontVariantNumeric = 'tabular-nums';
    var raf = 0;
    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      el.textContent = fmt(target);
    }
    function run() {
      var start = performance.now();
      var dur = 1500;
      (function tick(now) {
        var t = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - t, 4); // spring-like settle, no overshoot
        el.textContent = fmt(target * eased);
        raf = t < 1 ? requestAnimationFrame(tick) : 0;
      })(start);
    }
    if (!inViewport(el)) el.textContent = '0';
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) run();
        else stop();
      });
    }, { threshold: 0.6 });
    io.observe(el);
  }

  function scan(root) {
    root = root || document;
    wireCtas(root);
    if (!('IntersectionObserver' in window)) return;
    root.querySelectorAll('[data-lp-reveal]').forEach(setupReveal);
    root.querySelectorAll('[data-lp-count]').forEach(setupCount);
  }

  window.cLpEntMotion = { scan: scan, reduce: reduce, ease: EASE };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
